import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText, Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { usePatientTreatmentHistoryFiltered } from '@/hooks/useDental';
import type { TreatmentHistoryFilters } from '@/hooks/useDental';
import { HistoryTable } from './HistoryTable';
import type { Database } from '@/types/database.generated';

type TreatmentRow = Database['public']['Tables']['tooth_treatments']['Row'];

// ── Props ─────────────────────────────────────────────────────────────────────

interface HistoryPanelProps {
  patientId: string;
  clinicId: string;
  className?: string;
}

// ── CSV Export ────────────────────────────────────────────────────────────────

function downloadCsv(rows: TreatmentRow[]) {
  const header = ['Dato', 'Tand', 'Kode', 'Beskrivelse', 'Behandler', 'Klinik', 'Pris'];
  const csvRows = rows.map((r) => [
    r.treatment_date,
    r.tooth_fdi,
    r.treatment_type,
    `"${r.treatment_description.replace(/"/g, '""')}"`,
    r.performed_by,
    r.performed_at_other_practice
      ? r.other_practice_name
        ? `Anden klinik - ${r.other_practice_name}`
        : 'Anden klinik'
      : 'Egen',
    r.actual_cost ?? r.estimated_cost ?? '',
  ]);

  const content = [header, ...csvRows].map((row) => row.join(';')).join('\n');
  const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `behandlingshistorik-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Component ─────────────────────────────────────────────────────────────────

export function HistoryPanel({ patientId, className }: HistoryPanelProps) {
  const { t } = useTranslation();

  // Realtime subscription hoisted to DentalModulePage (single channel per mount).

  const [filters, setFilters] = useState<TreatmentHistoryFilters>({});
  const [freeText, setFreeText] = useState('');

  const activeFilters: TreatmentHistoryFilters = useMemo(
    () => ({ ...filters, freeText: freeText || null }),
    [filters, freeText],
  );

  const { data: allRows = [], isLoading } = usePatientTreatmentHistoryFiltered(
    patientId,
    activeFilters,
  );

  // Derive FDI list for filter dropdown
  const { data: allHistoryUnfiltered = [] } = usePatientTreatmentHistoryFiltered(patientId, {});
  const availableFdis = useMemo(() => {
    const s = new Set(allHistoryUnfiltered.map((r) => r.tooth_fdi));
    return [...s].sort();
  }, [allHistoryUnfiltered]);

  const setFilter = <K extends keyof TreatmentHistoryFilters>(
    key: K,
    value: TreatmentHistoryFilters[K],
  ) => setFilters((prev) => ({ ...prev, [key]: value }));

  const clearFilters = () => {
    setFilters({});
    setFreeText('');
  };

  const hasFilters =
    Object.values(filters).some((v) => v != null && v !== false) || Boolean(freeText);

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex-shrink-0 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          {t('dental.history.title')}
          {allRows.length > 0 && (
            <span className="ml-2 text-xs font-normal text-slate-400 dark:text-slate-500">
              {allRows.length} poster
            </span>
          )}
        </h3>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => downloadCsv(allRows)}
            disabled={allRows.length === 0}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-40"
          >
            <Download className="h-3.5 w-3.5" />
            {t('dental.history.exportCsv')}
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <FileText className="h-3.5 w-3.5" />
            {t('dental.history.exportPdf')}
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
        {/* Free-text */}
        <input
          type="text"
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder="Søg..."
          className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-36"
        />

        {/* Date from */}
        <input
          type="date"
          value={filters.dateFrom ?? ''}
          onChange={(e) => setFilter('dateFrom', e.target.value || null)}
          title={t('dental.history.filters.dateRange')}
          className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <span className="text-xs text-slate-400">–</span>
        <input
          type="date"
          value={filters.dateTo ?? ''}
          onChange={(e) => setFilter('dateTo', e.target.value || null)}
          className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />

        {/* Tooth filter */}
        <select
          value={filters.toothFdi ?? ''}
          onChange={(e) => setFilter('toothFdi', e.target.value || null)}
          className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">{t('dental.history.filters.tooth')}</option>
          {availableFdis.map((fdi) => (
            <option key={fdi} value={fdi}>
              {fdi}
            </option>
          ))}
        </select>

        {/* Klinik toggle */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() =>
              setFilter('onlyOtherPractice', filters.onlyOtherPractice ? undefined : true)
            }
            className={cn(
              'rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors',
              filters.onlyOtherPractice
                ? 'border-slate-400 bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-50',
            )}
          >
            {t('dental.history.filters.otherClinic')}
          </button>
          <button
            type="button"
            onClick={() =>
              setFilter('onlyOwnPractice', filters.onlyOwnPractice ? undefined : true)
            }
            className={cn(
              'rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors',
              filters.onlyOwnPractice
                ? 'border-indigo-400 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-50',
            )}
          >
            {t('dental.history.filters.ownClinic')}
          </button>
        </div>

        {hasFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            Ryd filtre
          </button>
        )}
      </div>

      {/* Table area */}
      <div className="flex-1 overflow-auto bg-white dark:bg-slate-900">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
          </div>
        ) : (
          <HistoryTable rows={allRows} isLoading={isLoading} />
        )}
      </div>
    </div>
  );
}
